<template>
    <fieldable v-loading="loading"
               :class="classes.root"
               :label="label"
               :name="name"
               :required="required && labelCompact"
               :errors="errors"
               :no="independent"
               :dark="dark">
        <!-- Label -->
        <template v-if="!labelCompact"
                  #label>
            <div :class="classes.label">
                <span v-if="required"
                      class="text-red">*</span>
                {{ label }}
            </div>
        </template>

        <div v-if="value.length">
            <div v-for="task in value"
                 :key="task.id"
                 class="flex f-x-between f-y-center _px-0.75 _py-0.65">
                <task-item :task="task"
                           with-image
                           no-go-to />
                <el-button class="_p-0.75"
                           icon="el-icon-delete fs-1.5"
                           type="danger" 
                           plain
                           @click="removeTask(task.id)" />
            </div>
        </div>

        <div v-else-if="showList">
            <div v-for="task in tasks"
                 :key="task.id"
                 class="space-y-0.25 _py-0.75 border-bottom-1 border-gray-300">
                <div class="f-y-center gap-0.75">
                    <span class="fs-0.75">№{{ task.number_in_project }}</span>
                    <task-status :task="task"
                                 :dark="dark" />
                </div>
                <div class="fs-0.875">
                    {{ task.name }}
                </div>
            </div>

            <el-button v-if="total > 3"
                       class="w-full _mt-0.75"
                       type="primary"
                       size="mini"
                       plain
                       @click="openTaskSelector">
                {{ labels.btnLabel }}
            </el-button>
        </div>

        <!-- Placeholder -->
        <div v-else
             :class="classes.placeholder"
             @click="openTaskSelector()">
            {{ placeholder }}
        </div>
    </fieldable>
</template>

<script>
import { mapMutations, mapActions } from 'vuex';

import { injectCommonFieldProps } from '@/utils/ui';
import { TASK_TYPES } from '@/utils/tasks';

import Fieldable from '@/components/fields/Fieldable.vue';
import TaskItem from '@/components/tasks/TaskItem.vue';
import TaskStatus from '@/components/tasks/TaskStatus.vue';
import { wordOfNumber } from '@/utils/format';

const labels = {
  label: 'Предписание которое послужило причиной отклонения работ:',
  placeholder: 'Нажмите, чтобы перейти к выбору предписания'
}

export default {
  name: 'SelectTaskDefectField',

  components: {
    Fieldable,
    TaskItem,
    TaskStatus
  },

  model: {
    prop: 'value',
    event: 'change'
  },

  props: {
    ...injectCommonFieldProps({ defaultLabel: labels.label, defaultPlaceholder: labels.placeholder }),

    value: { type: Array, default: () => [] },
    organizationId: { type: String, default: '' },
    showList: { type: Boolean, default: false },
    labelCompact: {  type: Boolean, default: false },
    dark: { type: Boolean, default: false }
  },

  data() {
    return {
      loading: false,

      taskSelectorVisible: false,
      filter: {},
      tasks: [],
      total: 0
    }
  },

  computed: {
    labels() {
      return {
        ...labels,
        btnLabel: `Еще ${this.total - 3} ${wordOfNumber(this.total - 3, ['предписание', 'предписания', 'предписаний'])}`
      }
    },
    classes() {
      return { 
        root: {
          'w-full space-y-0.75': true
        },
        label: {
          'fs-1 text-el-dark': true
        },
        placeholder: {
          'border-1 border-accent dashed cursor-pointer text-accent fs-0.75 f-center h-4 border-box': true
        }
      }
    }
  },

  mounted() {
    this.filter = {
      project_id: this.$route.params.projectId,
      type: TASK_TYPES.DEFECTS_AND_VIOLATIONS,
      worker_organization_id: this.organizationId,
      allowed_for_rejection_of_acceptance: 1 
    }

    this.showList && this.fetch()
  },
  
  methods: {
    ...mapActions('tasks', ['getTasks']),
    ...mapMutations({
      showDialog: 'dialog/SHOW_DIALOG'
    }),

    fetch() {
      this.loading = true
  
      return this.getTasks({
        params: {
          page: { number: 1, size: 3 },
          filter: this.filter,
          sort: '-created_at',
          include: [
            'workerOrganization', 'creator', 'project', 'members.user.organization', 'memberInitiator.user.organization', 'memberWorkers.user.organization',
            'memberInspectors.user.organization', 'acceptanceOfWorkContractor.organization', 'defectGeneralContractor.organization'
          ]
        }
      }).then(resp => {
        this.tasks = resp.data.data
        this.total = resp.data.meta.total
      }).finally(() => {
        this.loading = false
      })
    },

    openTaskSelector() {
      this.showDialog({
        dialogName: 'task-defect-selector',
        action: this.selectTask,
        payload: {
          filter: this.filter,
          readonly: this.readonly
        },
        multiple: true
      })
    },

    selectTask(tasks) {
      this.$emit('change', tasks)
    },

    removeTask(id) {
      const tasks = this.value.filter(x => x.id !== id)
      this.selectTask(tasks)
    }
  }
}
</script>
